import {
  ColumnTaxRowStatus,
  ColumnTaxTile,
  Maybe,
  TileStatus,
} from '@varo/graphql-schema-types';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { ColumnTaxCardProps } from 'shared/components/vxl/cards/ColumnTaxTile/ColumnTaxCard';
import { ListItemLeadingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemLeadingAccessory';
import { ListItemTrailingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemTrailingAccessory';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { tagColorMapper } from 'shared/utils/tagColorMapper/tagColorMapper';

interface ColumnTaxTrackerTileNode extends VXLComponent {
  id: string;
  name: VXLComponentId.COLUMN_TAX_CARD;
  props: ColumnTaxCardProps;
}

const getIconAndColor = (rowStatus: ColumnTaxRowStatus) => {
  switch (rowStatus) {
    case ColumnTaxRowStatus.Accepted:
      return {
        icon: 'LIST_CHECK_MARK',
        iconColor: theme.color.status.positive,
      };
    case ColumnTaxRowStatus.Rejected:
      return {
        icon: 'LIST_X_MARK',
        iconColor: theme.color.status.negative,
      };
    case ColumnTaxRowStatus.Retryable:
      return {
        icon: 'LIST_WARNING_MARK',
        iconColor: theme.color.status.warning,
      };
    case ColumnTaxRowStatus.Submitted:
    default:
      return {
        icon: 'CIRCLE',
        iconColor: theme.color.text.tertiary,
      };
  }
};

const columnTaxTile = (
  tile: ColumnTaxTile,
  { navigateToRoute }: HomeCardDependencies,
): Maybe<ColumnTaxTrackerTileNode> => {
  if (tile.status !== TileStatus.Loaded) {
    return null;
  }
  const isBodyTitleTrailingTextGray = tile.columnTaxRows?.some(
    (c) => c.ColumnTaxRowStatus === ColumnTaxRowStatus.Submitted,
  );
  const bodyTextTrailingColor = isBodyTitleTrailingTextGray
    ? theme.color.text.secondary
    : theme.color.text.primary;

  const rowData = tile.columnTaxRows?.map((columnTaxRow) => {
    const { icon, iconColor } = getIconAndColor(
      columnTaxRow.ColumnTaxRowStatus,
    );
    const id = columnTaxRow.id;

    return {
      id,
      primaryCopy: columnTaxRow.leadingText,
      showsChevron: false,
      leadingAccessory: {
        id,
        name: VXLComponentId.ICON,
        props: {
          variant: 'MD',
          iconKey: icon,
          iconColor: iconColor,
        },
      } satisfies ListItemLeadingAccessoryProps,
      trailingAccessory: {
        id,
        name: VXLComponentId.LIST_ITEM_TEXT_STACK,
        props: {
          primaryCopy: columnTaxRow.trailingText,
          primaryCopyColor:
            columnTaxRow.ColumnTaxRowStatus === ColumnTaxRowStatus.Submitted
              ? theme.color.text.secondary
              : theme.color.text.primary,
        },
      } satisfies ListItemTrailingAccessoryProps,
    };
  });

  return {
    id: tile.id,
    name: VXLComponentId.COLUMN_TAX_CARD,
    props: {
      headerText: tile.headerText,
      headerTag: {
        text: tile.headerTag.text,
        type: tagColorMapper(tile.headerTag.tagColor),
      },
      bodyTitleText: tile.bodyTitleText,
      bodyTitleTrailingText: tile.bodyTitleTrailingText ?? undefined,
      bodyTextTrailingColor: bodyTextTrailingColor,
      bodyDescriptionText: tile.bodyDescriptionText ?? undefined,
      rowData: rowData,
      footerButtonText: tile.footerButtonCopy,
      footerButtonAction: () => {
        navigateToRoute(tile.footerButtonAction);
      },
    },
  };
};

export default columnTaxTile;
